import { Navigate, useSearchParams } from "react-router";
import { Box, useTheme } from "@mui/material";
import { FoodsTabs, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useApplicationParameters } from "src/api/client-api/appParams";
import {
    useGetNetworkFilterOptions,
    useGetNetworkMembers,
    useGetNetworkQuestionnaireProgress,
} from "src/api/client-api/network/useNetworkLists";
import { ActiveOrganizationContextHeader } from "src/components/ActiveOrganizationContextHeader";
import { ORGANIZATION, useFoodpilotContext, VIEW_NETWORK_TRACKING } from "src/context/FoodpilotContext";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { NetworkMembersList } from "./NetworkMembersList";
import { NetworkQuestionnaireProgressList } from "./NetworkQuestionnaireProgressList";
import { GRID_PANEL_ID, NetworkError, type NetworkTab, tabElementId } from "./networkListParts";
import { NETWORK_TAB_PARAM } from "./useNetworkListParams";

const tabLabel = (label: string, count: { isReady: boolean; totalItems: number; errorStatus: unknown }) =>
    count.isReady && count.errorStatus === null ? `${label} - ${count.totalItems}` : label;

export const NetworkPage = () => {
    const { isFeatureActivated } = useApplicationParameters();
    const { canUser } = useFoodpilotContext();

    if (!isFeatureActivated("FS-network-tracking") || !canUser(VIEW_NETWORK_TRACKING, ORGANIZATION)) {
        return <Navigate to="/" replace />;
    }

    return <NetworkPageContent />;
};

const NetworkPageContent = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { isMobile } = useResponsiveBreakpoints();
    const [searchParams, setSearchParams] = useSearchParams();
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const organizationId = selectedCompany?.id;
    const activeTab: NetworkTab = searchParams.get(NETWORK_TAB_PARAM) === "users" ? "users" : "organizations";
    const filterOptions = useGetNetworkFilterOptions(organizationId);

    const organizationsCount = useGetNetworkQuestionnaireProgress({
        organizationId,
        params: { campaign: currentCampaign?.id, itemsPerPage: 1 },
    });
    const usersCount = useGetNetworkMembers({ organizationId, params: { itemsPerPage: 1 } });

    const selectTab = (tab: NetworkTab) => setSearchParams(new URLSearchParams({ [NETWORK_TAB_PARAM]: tab }));
    const tabs = [
        {
            id: "organizations" as const,
            name: tabLabel(t("network.tabs.questionnaires", "Questionnaires"), organizationsCount),
            TabProps: {
                id: tabElementId("organizations"),
                "aria-controls": GRID_PANEL_ID,
                onClick: () => selectTab("organizations"),
            },
        },
        {
            id: "users" as const,
            name: tabLabel(t("network.tabs.users", "Users"), usersCount),
            TabProps: { id: tabElementId("users"), "aria-controls": GRID_PANEL_ID, onClick: () => selectTab("users") },
        },
    ];

    return (
        <Box
            sx={{
                marginInlineEnd: "auto",
                marginInlineStart:
                    isMobile ? "auto" : `calc(var(--app-navigation-overlap, ${theme.spacing(2.5)}) * -1)`,
            }}
        >
            <Box sx={{ backgroundColor: theme.custom.grey[100], position: "relative" }}>
                <Box sx={{ paddingInline: theme.spacing(6), paddingTop: theme.spacing(3) }}>
                    <ActiveOrganizationContextHeader
                        title={<Typography variant="h1">{t("network.title", "Network tracking")}</Typography>}
                    />
                </Box>
                <Box sx={{ marginTop: theme.spacing(1), paddingInline: theme.spacing(6) }}>
                    <FoodsTabs
                        forceScrollable
                        tabs={tabs}
                        tabVariant="primary"
                        selectedTabIndex={activeTab === "users" ? 1 : 0}
                        TabsProps={{
                            "aria-label": t("network.title", "Network tracking"),
                            slotProps: { list: { sx: { gap: theme.spacing(4.5) } } },
                        }}
                    />
                </Box>
                <Box
                    aria-hidden
                    sx={{
                        borderBottomColor: theme.custom.strokes.containerDivider.color,
                        borderBottomStyle: theme.custom.strokes.containerDivider.style,
                        borderBottomWidth: theme.custom.strokes.containerDivider.width,
                        bottom: 0,
                        left: 0,
                        pointerEvents: "none",
                        position: "absolute",
                        right: 0,
                    }}
                />
            </Box>
            {filterOptions.errorStatus !== null && (
                <NetworkError status={filterOptions.errorStatus} onRetry={filterOptions.refetch} />
            )}
            {activeTab === "organizations" ?
                <NetworkQuestionnaireProgressList filterOptions={filterOptions} />
            :   <NetworkMembersList filterOptions={filterOptions} />}
        </Box>
    );
};
